import { useMemo, useEffect } from "react";
import { useLazyQuery } from "@apollo/client/react";
import type { SortingState } from "@salestrak/ui";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  DashboardActivityDrilldownDocument,
  DashboardActivityGroupSummariesDocument,
  DashboardAgeReasonGroupSummariesDocument,
  type DashboardDrilldownActivity,
  type DashboardDrilldownIssue,
  DashboardIssueDrilldownDocument,
  DashboardIssueGroupSummariesDocument,
  type SortDirection,
} from "#app/lib/data";
import { usePagedConnection } from "#app/lib/hooks/use-paged-connection";
import type { DrilldownType } from "../components/DrilldownDialog";
import type { Omittable } from "@salestrak/ui";

// Resolve once at module level — timezone does not change mid-session
const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const DRILLDOWN_PAGE_SIZE = 50;

// Maps client-side accessorKey → backend sortField for activity drilldowns
const ACTIVITY_SORT_MAP: Record<string, string> = {
  project: "issueName",
  ageUpdates: "creationDate",
  _currentAge: "issueAgeDate",
  lastAgeUpdate: "creationDate",
  projectCreated: "issueCreationDate",
  lastReason: "reasonName",
  date: "creationDate",
  member: "createdByName",
  from: "oldStageName",
  to: "newStageName",
  reason: "reasonName",
  time: "creationDate",
  address: "issueAddress",
  issueName: "issueName",
  action: "action",
};

interface UseDrilldownQueriesParams {
  drilldown: DrilldownType | null;
  startDate: string;
  endDate: string;
  teamIds: string[] | undefined;
  memberIds: string[] | undefined;
  sorting: SortingState;
}

export function useDrilldownQueries({
  drilldown,
  startDate,
  endDate,
  teamIds,
  memberIds,
  sorting,
}: UseDrilldownQueriesParams) {
  // Determine pagination strategy per drilldown kind
  const isPaginatedActivity =
    !!drilldown &&
    ["stageChanges", "repActivity"].includes(drilldown.kind);

  // Resolve sort field for backend
  const sortField = useMemo(() => {
    const id = sorting[0]?.id;
    if (!id) return undefined;
    return ACTIVITY_SORT_MAP[id] ?? id;
  }, [sorting]);
  const sortDirection = useMemo(
    (): SortDirection | undefined =>
      sorting[0]?.id ? (sorting[0].desc ? "desc" : "asc") : undefined,
    [sorting],
  );

  // Lazy queries for drill-down data (paginated activities use usePagedConnection below)
  const [fetchIssues, issuesResult] = useLazyQuery(
    DashboardIssueDrilldownDocument,
    { notifyOnNetworkStatusChange: true },
  );
  const [fetchAgeReasonGroupSummaries, ageReasonGroupSummariesResult] =
    useLazyQuery(DashboardAgeReasonGroupSummariesDocument, {
      notifyOnNetworkStatusChange: true,
    });
  const [fetchIssueGroupSummaries, issueGroupSummariesResult] = useLazyQuery(
    DashboardIssueGroupSummariesDocument,
    { notifyOnNetworkStatusChange: true },
  );
  const [fetchActivityGroupSummaries, activityGroupSummariesResult] =
    useLazyQuery(DashboardActivityGroupSummariesDocument, {
      notifyOnNetworkStatusChange: true,
    });

  // Fire summary queries for specialized (non-paginated) drilldown kinds.
  useEffect(() => {
    if (!drilldown) return;
    const kind = drilldown.kind;

    if (kind === "stageBreakdown" || kind === "workflowIssues") {
      fetchIssueGroupSummaries({
        variables: {
          input: {
            startDate,
            endDate,
            teamIds,
            memberIds,
            stageName: kind === "stageBreakdown" ? drilldown.stage : undefined,
            workflowName:
              kind === "workflowIssues" ? drilldown.workflow : undefined,
          },
        },
      });
    } else if (kind === "dailyActivity") {
      fetchAgeReasonGroupSummaries({
        variables: {
          input: {
            startDate,
            endDate,
            teamIds,
            memberIds,
            subject: "age",
            subjectStartDate: drilldown.date,
            subjectEndDate: drilldown.endDate,
            timezone: TIMEZONE,
          },
        },
      });
      fetchIssues({
        variables: {
          input: {
            startDate,
            endDate,
            teamIds,
            memberIds,
            subjectStartDate: drilldown.date,
            subjectEndDate: drilldown.endDate,
            timezone: TIMEZONE,
          },
        },
      });
    } else if (kind === "ageReasons") {
      fetchAgeReasonGroupSummaries({
        variables: {
          input: { startDate, endDate, teamIds, memberIds, subject: "age", timezone: TIMEZONE },
        },
      });
    } else if (kind === "goldenHour") {
      fetchAgeReasonGroupSummaries({
        variables: {
          input: {
            startDate,
            endDate,
            teamIds,
            memberIds,
            subject: "age",
            hour: drilldown.hour,
            timezone: TIMEZONE,
          },
        },
      });
    } else if (kind === "targetMetric") {
      fetchActivityGroupSummaries({
        variables: {
          input: {
            startDate,
            endDate,
            teamIds,
            memberIds,
            targetMetricOnly: true,
            timezone: TIMEZONE,
          },
        },
      });
    }
  }, [
    drilldown,
    startDate,
    endDate,
    teamIds,
    memberIds,
    fetchAgeReasonGroupSummaries,
    fetchIssueGroupSummaries,
    fetchIssues,
    fetchActivityGroupSummaries,
  ]);

  // Compose the activity drilldown input. Only meaningful when this kind
  // is a paginated activity drilldown — usePagedConnection is gated on that.
  const activityInput = useMemo(() => {
    if (!drilldown || !isPaginatedActivity) return null;
    const kind = drilldown.kind;
    // The filter bar leaves every narrowing field unset until the user picks
    // one, and the server reads an absent key and an explicit `undefined` the
    // same way — so the members relax.
    const input: Omittable<{
      startDate: string;
      endDate: string;
      teamIds?: string[];
      memberIds?: string[];
      sortField?: string;
      sortDirection?: SortDirection;
      subject?: string;
      userId?: string;
    }> = {
      startDate,
      endDate,
      teamIds,
      memberIds,
      sortField,
      sortDirection,
    };
    if (kind === "stageChanges") {
      input.subject = "stage";
    } else if (kind === "repActivity") {
      input.subject = "age";
      input.userId = drilldown.userId;
    }
    return input;
  }, [drilldown, isPaginatedActivity, startDate, endDate, teamIds, memberIds, sortField, sortDirection]);

  // Per-page paginated activity feed for stageChanges / repActivity drilldowns.
  const activitiesPaged = usePagedConnection<DashboardDrilldownActivity>({
    document: DashboardActivityDrilldownDocument,
    variables: useMemo(
      () => ({ input: activityInput ?? {} }),
      [activityInput],
    ),
    pageSize: DRILLDOWN_PAGE_SIZE,
    skipQuery: !activityInput,
    getConnection: (d) => d?.dashboardActivityDrilldown,
  });

  const activityData: DashboardDrilldownActivity[] = activitiesPaged.items;
  const activityTotal = activitiesPaged.total;
  const fetchPageActivities = activitiesPaged.fetchPage;

  const issueData: DashboardDrilldownIssue[] = useMemo(
    () =>
      issuesResult.data?.dashboardIssueDrilldown?.edges?.map((e) => e.node) ??
      [],
    [issuesResult.data],
  );
  const ageReasonGroupSummaries =
    ageReasonGroupSummariesResult.data?.dashboardAgeReasonGroupSummaries ?? [];
  const issueGroupSummaries =
    issueGroupSummariesResult.data?.dashboardIssueGroupSummaries ?? [];
  const activityGroupSummaries =
    activityGroupSummariesResult.data?.dashboardActivityGroupSummaries ?? [];

  // Surface query errors as toasts
  const queryError =
    issuesResult.error ??
    ageReasonGroupSummariesResult.error ??
    issueGroupSummariesResult.error ??
    activityGroupSummariesResult.error;
  useEffect(() => {
    if (queryError) toastMutationError(queryError, "Failed to load drilldown data");
  }, [queryError]);

  const isDailyActivity = drilldown?.kind === "dailyActivity";
  const isStageBreakdown = drilldown?.kind === "stageBreakdown";
  const isWorkflowIssues = drilldown?.kind === "workflowIssues";
  const isAgeReasons = drilldown?.kind === "ageReasons";
  const isGoldenHour = drilldown?.kind === "goldenHour";
  const isTargetMetric = drilldown?.kind === "targetMetric";

  const drilldownLoading =
    (!isAgeReasons &&
      !isGoldenHour &&
      !isDailyActivity &&
      !isStageBreakdown &&
      !isWorkflowIssues &&
      !isTargetMetric &&
      activitiesPaged.loading) ||
    (ageReasonGroupSummariesResult.loading &&
      !ageReasonGroupSummariesResult.data) ||
    (issueGroupSummariesResult.loading && !issueGroupSummariesResult.data) ||
    (activityGroupSummariesResult.loading &&
      !activityGroupSummariesResult.data &&
      isTargetMetric) ||
    (isDailyActivity && issuesResult.loading);

  const usesSpecializedView =
    isDailyActivity ||
    isStageBreakdown ||
    isWorkflowIssues ||
    isAgeReasons ||
    isGoldenHour ||
    isTargetMetric;

  return {
    activityData,
    activityTotal,
    issueData,
    ageReasonGroupSummaries,
    issueGroupSummaries,
    activityGroupSummaries,
    drilldownLoading,
    fetchPageActivities,
    isPaginatedActivity,
    isDailyActivity,
    isStageBreakdown,
    isWorkflowIssues,
    isAgeReasons,
    isGoldenHour,
    isTargetMetric,
    usesSpecializedView,
    sortField,
    sortDirection,
    timezone: TIMEZONE,
  };
}

export { ACTIVITY_SORT_MAP, TIMEZONE, DRILLDOWN_PAGE_SIZE };
